import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AsideService, ViewAssetAsideData } from '../../../core/services/aside.service';
import { AssetService } from '../../../core/services/asset.service';
import { PermissionsService } from '../../../core/services/permissions.service';

import { VehicleStatusType, VehicleStatusOptionMap } from '../../../core/enums/vehicle-status.enum';
import { VehicleType } from '../../../core/enums/vehicle.enum';
import { LucideIconComponent } from '../../../shared/ui/lucide-icon/lucide-icon.component';
import { ButtonComponent } from '../../../shared/ui/button/button.component';
import { HeadingComponent } from '../../../shared/ui/typography/heading/heading.component';
import { TextComponent } from '../../../shared/ui/typography/text/text.component';
import { BadgeComponent } from '../../../shared/ui/badge/badge.component';
import { ColorVariant } from '../../../core/enums/types';
import { ModalService } from '../../../core/services/modal.service';
import { AddVehicleModalComponent } from '../add-vehicle-modal/add-vehicle-modal.component';
import { ChangeVehicleStatusModalComponent } from '../change-vehicle-status-modal/change-vehicle-status-modal.component';
import { UploadDocumentModalComponent } from '../../../shared/ui/forms/upload-document-modal/upload-document-modal.component';
import { DocumentViewerModalComponent } from '../../../shared/ui/document-viewer-modal/document-viewer-modal.component';
import { VehicleDocument, VehiclePhoto } from '../../../core/models/vehicle.model';

@Component({
  selector: 'app-fleet-aside',
  standalone: true,
  imports: [CommonModule, 
    LucideIconComponent,
    ButtonComponent,
    HeadingComponent,
    TextComponent,
    BadgeComponent],
  templateUrl: './fleet-aside.component.html',
})
export class FleetAsideComponent {
  private readonly asideService = inject(AsideService);
  private readonly assetService = inject(AssetService);
  private readonly modalService = inject(ModalService);
  readonly perms = inject(PermissionsService);

  readonly context = this.asideService.asideContext;
  
  // Computed asset directly from the service cache using the context ID
  readonly asset = computed(() => {
    const ctx = this.context();
    if (ctx?.type !== 'view-asset') return null;
    return this.assetService.getAssetById((ctx.data as ViewAssetAsideData).id) || null;
  });

  readonly incompleteReasons = computed(() => {
    const a = this.asset();
    if (!a) return [];
    return this.assetService.getIncompleteReasons(a);
  });

  getTypeLabel(type: VehicleType): string {
    const map: Record<string, string> = {
      TRUCK: 'Truck',
      TRAILER: 'Trailer',
      OTHER: 'Other',
    };
    return map[type] || type;
  }

  getAssetTypeLabel(asset: any): string {
    if (asset.type === 'TRAILER' && asset.specs?.trailerType) {
      let label = asset.specs.trailerType;
      if (asset.specs.trailerLengthFt) {
        label += ` - ${asset.specs.trailerLengthFt} ft`;
      }
      return label;
    }
    return this.getTypeLabel(asset.type);
  }

  readonly visibleDocuments = computed(() => {
    const a = this.asset();
    if (!a?.documents) return [];
    
    // Admins (or those who can view fleet pricing) can see all documents
    if (this.perms.canViewFleetPricing()) {
      return a.documents;
    }
    
    // Non-admins cannot see Finance, Lease, or Title documents
    return a.documents.filter(doc => 
      doc.title !== 'Title' && 
      doc.title !== 'Lease Agreement' && 
      doc.title !== 'Loan Document'
    );
  });

  getTypeIcon(type: VehicleType): string {
    const map: Record<string, string> = {
      TRUCK: 'truck',
      TRAILER: 'container',
      OTHER: 'package',
    };
    return map[type] || 'package';
  }

  getTypeBadgeVariant(type: VehicleType): ColorVariant {
    const map: Record<string, ColorVariant> = {
      TRUCK: 'info',
      TRAILER: 'warning',
      OTHER: 'default',
    };
    return map[type] || 'default';
  }

  getStatusLabel(status: VehicleStatusType): string {
    return VehicleStatusOptionMap[status]?.label || status;
  }

  getStatusBadgeVariant(status: VehicleStatusType): ColorVariant {
    return (VehicleStatusOptionMap[status]?.color as ColorVariant) || 'default';
  }

  editAsset(): void {
    const a = this.asset();
    if (!a) return;
    
    this.modalService.open(AddVehicleModalComponent, {
      title: 'Edit Vehicle',
      subtitle: 'Update details for this vehicle',
      icon: 'pencil',
      size: 'lg',
      data: { assetToEdit: a },
    });
  }

  changeStatus(): void {
    const a = this.asset();
    if (!a) return;

    this.modalService.open(ChangeVehicleStatusModalComponent, {
      title: 'Change Status',
      subtitle: 'Update the current status of this vehicle',
      icon: 'activity',
      size: 'md',
      data: { asset: a },
    });
  }

  uploadDocument(): void {
    const a = this.asset();
    if (!a) return;

    this.modalService.open(UploadDocumentModalComponent, {
      title: 'Upload Document',
      size: 'md',
      data: {
        description: 'Upload a document like Title, Registration, or Lease Agreement.',
        uploadPath: `vehicles/${a.id}/documents`,
        typeOptions: [
          { value: 'Title', label: 'Title' },
          { value: 'Registration', label: 'Registration' },
          { value: 'Lease Agreement', label: 'Lease Agreement' },
          { value: 'Loan Document', label: 'Loan Document' },
          { value: 'Insurance', label: 'Insurance' },
          { value: 'Inspection', label: 'Inspection' },
          { value: 'Other', label: 'Other' }
        ],
        accept: 'application/pdf,image/*',
        onSuccess: (res: { type: string; url: string }) => {
          const newDoc: VehicleDocument = {
            id: crypto.randomUUID(),
            title: res.type,
            filePath: res.url,
            uploadedAt: Date.now()
          };
          const docs = a.documents ? [...a.documents, newDoc] : [newDoc];
          this.assetService.updateAsset(a.id, { documents: docs });
        }
      }
    });
  }

  uploadPhoto(): void {
    const a = this.asset();
    if (!a) return;

    this.modalService.open(UploadDocumentModalComponent, {
      title: 'Upload Photo',
      size: 'md',
      data: {
        description: 'Upload pictures of the vehicle.',
        uploadPath: `vehicles/${a.id}/photos`,
        typeOptions: [
          { value: 'Front', label: 'Front' },
          { value: 'Left', label: 'Left Side' },
          { value: 'Right', label: 'Right Side' },
          { value: 'Rear', label: 'Rear' },
          { value: 'Interior', label: 'Interior' },
          { value: 'Other', label: 'Other' }
        ],
        accept: 'image/*',
        allowedExtensions: ['.jpg', '.jpeg', '.png', '.webp'],
        onSuccess: (res: { type: string; url: string }) => {
          const newPhoto: VehiclePhoto = {
            id: crypto.randomUUID(),
            side: res.type,
            filePath: res.url,
            uploadedAt: Date.now()
          };
          const photos = a.photos ? [...a.photos, newPhoto] : [newPhoto];
          this.assetService.updateAsset(a.id, { photos: photos });
        }
      }
    });
  }

  viewDocument(url: string, type: 'pdf' | 'image', filename: string): void {
    this.modalService.open(DocumentViewerModalComponent, {
      title: filename,
      size: 'xl',
      data: {
        url,
        documentType: type,
        filename
      }
    });
  }
}
